import { useAtom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useTranslation } from 'react-i18next';

import { surfaceDifferenceBodyVisibilityAtom } from '@/atoms/surfaceDifference.atom';
import { DeviationColorbar } from '@/components/DeviationColorbar';
import { useLayerSettings } from '@/hooks/useLayerSettings';
import { resolveSurfaceDifferenceShading } from '@/lib/surfaceDifference/shading';
import { StationVolumesChip } from '@/components/StationVolumesChip';
import { DOCK_PAD, DockKpi, DockPanel } from '@/components/dock/DockHeaderParts';
import { DockPanelActions } from '@/components/dock/DockPanelActions';
import { Checkbox } from '@/components/ui/checkbox';
import { type Criterion, type Verdict, VerdictBadge, VerdictChip } from '@/components/VerdictChip';
import { mToMm } from '@/lib/surfaceExpectation';
import { formatM3, formatM3Row } from '@/lib/stationVolumes/format';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';
import { cn } from '@/lib/utils';
import type { StationVolumesMeta } from '@/lib/stationVolumes/types';

interface SurfaceStat {
  featureId: number;
  featureName: string;
  cutVolumeM3: number;
  fillVolumeM3: number;
  netVolumeM3: number;
  areaM2: number;
}

/**
 * `stats.expectation`, computed grid-wide over the whole overlap
 * (`scripts/expectation.py`). Absent on every run made before this feature
 * and on any run made with mode `NONE` — the panel renders exactly as today
 * when it is missing (Task 5).
 *
 * `meanThicknessM`/`minThicknessM`/`maxThicknessM`/`meanDeviationM`/
 * `stdDeviationM`/`underToleranceM`/`overToleranceM` are `null` when nothing
 * was measured (`measuredCells === 0`) — absence is a state, never a zero
 * (design doc §4.3).
 */
export interface ExpectationMeta {
  mode: 'MATCH' | 'CONSTANT' | 'SURFACES';
  /** `null` for SURFACES mode (`surface_difference.py:748` writes no single
   *  scalar there — the expectation is a second full surface, not a number).
   *  `0.0` for MATCH (a sentinel meaning "should match exactly", not a
   *  measurement). Only CONSTANT ever carries a real design value. */
  thicknessM: number | null;
  underToleranceM: number | null;
  overToleranceM: number | null;
  failAreaPct: number;
  notMeasuredAreaPct: number;
  measuredCells: number;
  meanThicknessM: number | null;
  minThicknessM: number | null;
  maxThicknessM: number | null;
  meanDeviationM: number | null;
  stdDeviationM: number | null;
  underAreaM2: number;
  deficitVolumeM3: number;
  surplusVolumeM3: number;
  crossingCellCount: number;
  crossingPct: number;
  underPct: number;
  inSpecPct: number;
  overPct: number;
  criteria: Criterion[];
  verdict: Verdict;
}

export interface SurfaceDifferenceMeta {
  stats: {
    cutVolumeM3: number;
    fillVolumeM3: number;
    netVolumeM3: number;
    areaM2: number;
    coveragePct: number;
    requestedResolution: number;
    effectiveResolution: number;
    surfaces?: SurfaceStat[];
    expectation?: ExpectationMeta;
  };
  range?: [number, number];
  colormap?: string;
  topLayerName?: string;
  bottomLayerName?: string;
  /** Summary-only station-volumes block (~900 bytes); the per-bin array lives
   *  in the `station_volumes.json` byproduct and is fetched when the dock
   *  opens. Absent on runs where station volumes were not requested. */
  stationVolumes?: StationVolumesMeta | null;
}

// localStorage, matching `DeviationVolumePanel`'s panel state directly above
// this one in the same column — both default to `expanded` and persist the
// same way, so a collapse survives a browser restart identically on either
// card instead of one remembering across restarts and the other only for
// the tab.
const storage = createJSONStorage<'collapsed' | 'expanded'>(() => localStorage);
const panelStateAtom = atomFamily((layerId: string) =>
  atomWithStorage<'collapsed' | 'expanded'>(`@actegon.gis.surfaceDifference.${layerId}`, 'expanded', storage),
);

/**
 * A ROW of related volumes at ONE precision, picked from the largest of them.
 *
 * The rule itself lives in `lib/stationVolumes/format.ts` so this panel and the
 * station-volumes dock cannot drift apart on decimals (ruling G9). What this
 * panel got wrong was calling the SINGLE-figure `formatM3` once per KPI, which
 * printed `Cut 34.732 m³` beside `Fill 1121.0 m³` in the same row — three
 * decimals claiming millimetre precision on an earthworks volume, next to a
 * figure rounded to 100 litres. `formatM3Row`'s own doc comment has said why
 * since it was written: one precision per row is the honest presentation.
 *
 * Undefined members keep their em dash and are pooled as 0, so a missing figure
 * cannot drag the row's precision either way.
 */
const m3Row = (values: (number | undefined)[]): string[] => {
  const row = formatM3Row(values.map((v) => (v == null || !Number.isFinite(v) ? 0 : v)));
  return values.map((v, i) => (v == null || !Number.isFinite(v) ? '—' : `${row[i]} m³`));
};

const m2 = (v: number | undefined) => (v == null ? '—' : `${v.toFixed(1)} m²`);

/**
 * The collapsed-state header summary, matching `DeviationVolumePanel`'s own:
 * that panel prints its Net figure beside the toggle so collapsing a card
 * doesn't hide every number on it. Same single-figure formatter
 * (`formatM3`, one precision on its own rather than the row's shared one —
 * there is no row here, just the one number), same explicit `+` for a
 * positive value.
 */
const formatNet = (v: number | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}${formatM3(v)} m³`;
};

export function SurfaceDifferencePanel({
  projectId,
  layerId,
  layerName,
  meta,
}: {
  /** The report button's project — this panel gained one so it could carry
   *  the same PDF button `DeviationVolumePanel` has. */
  projectId: string;
  layerId: string;
  layerName: string;
  meta: SurfaceDifferenceMeta;
}) {
  const { t } = useTranslation('project');
  const [state, setState] = useAtom(panelStateAtom(layerId));
  const [vis, setVis] = useAtom(surfaceDifferenceBodyVisibilityAtom(layerId));
  // The legend draws what the mesh is painted with: the layer settings'
  // colormap and display range (GLBModel repaints from the same resolver),
  // falling back to the run's own colormap and range.
  const { settings } = useLayerSettings(layerId);
  const shading = resolveSurfaceDifferenceShading(settings, meta);
  const s = meta?.stats;
  // Defensive: metadata written by an older postprocess build (or a partially
  // written layer) may not carry `stats` yet — render nothing rather than
  // crashing the whole viewer overlay.
  if (!s) return null;
  const e = s.expectation;
  // Cut / Fill / Net at ONE precision, chosen from the largest of the three —
  // exactly what `DeviationVolumePanel` does with its own trio.
  const kpi = m3Row([s.cutVolumeM3, s.fillVolumeM3, s.netVolumeM3]);
  const coarsened = s.effectiveResolution > s.requestedResolution * 1.001;

  return (
    <div className='pointer-events-auto flex flex-col gap-2'>
      <DeviationColorbar
        layerName={`${layerName} · ${t('surfaceDifference.panel.legend')}`}
        min={shading.min}
        max={shading.max}
        gradientName={shading.gradient}
        // `dz = top − bottom`, so the CUT side of this bar is the negative
        // one — the opposite end from a deviation bar. Saying which KIND of
        // run it is, rather than which end is red, is what stops the two
        // legends contradicting each other (T4).
        kind='surfaceDifference'
      />
      {/* `.sv-palette` brings in the station-volumes `--sv-*` variables (light
          and dark) without the dock's own surface, so the cut/fill figures
          here are the SAME two hues the chart, the table and the 3D bands
          use. They were `text-rose-600` / `text-sky-600`, which is a third
          pair of ad-hoc colours for the one quantity this feature is about. */}
      <style>{SV_TOKENS}</style>
      {/* The card, its header and its collapse behaviour are `DockPanel`'s,
          shared with `DeviationVolumePanel` directly above this one in the
          same column — including the `w-full` that keeps this panel as wide as
          the colorbar above it (a fixed width read as ragged; the reasoning
          now lives on `DockPanel` so it survives a tidy-up here). `sv-palette`
          is this panel's own: it brings in the `--sv-*` variables the cut/fill
          figures below are coloured with. */}
      <DockPanel
        className='sv-palette'
        expanded={state === 'expanded'}
        onToggle={() => setState(state === 'expanded' ? 'collapsed' : 'expanded')}
        toggleLabel={state === 'expanded' ? t('dockPanel.collapse') : t('dockPanel.expand')}
        title={t('dockPanel.title')}
        summary={
          state !== 'expanded' && (
            <>
              <span className='ml-2 shrink-0 font-mono tabular-nums whitespace-nowrap text-slate-700 dark:text-stone-200'>
                {formatNet(s.netVolumeM3)}
              </span>
              {/* THE VERDICT, in the collapsed header.
                  The compliance row below used to sit outside the collapse
                  gate for one stated reason: a FAIL verdict is the one thing
                  on this panel a user must not have to expand a card to see.
                  That reason is right, but keeping a seven-metric row plus a
                  popover chip permanently on screen is not what it argues for
                  — it is why this card never actually collapsed while the one
                  above it did. The verdict itself belongs here, beside the
                  net, where it is visible in the collapsed state AND takes one
                  line. `DeviationVolumePanel`'s summary has the same shape:
                  net, then one qualifying figure. */}
              {e && <VerdictBadge verdict={e.verdict} />}
            </>
          )
        }
        actions={
          // Layer name + PDF button, from the ONE component both panels use.
          // The name used to be a bare `flex-1 text-right` span here and a
          // `min-w-0` span inside a flex row on the deviation panel; two
          // roundings of "make the headers match" later, they are the same
          // element because there is only one of it.
          <DockPanelActions projectId={projectId} layerId={layerId} layerName={layerName} kind='surfaceDifference' />
        }>
        {/* EVERYTHING below the header is gated on `expanded` — the card
            collapses to its header line, exactly as `DeviationVolumePanel`
            does. Three blocks used to render outside this gate (the KPI row,
            the station chip and the compliance row), which is why the owner
            reported that this panel "does not collapse properly": collapsing
            it hid the resolution table and the two checkboxes and left the
            rest of the card exactly where it was. */}
        {state === 'expanded' && (
          <>
            {/* `items-end` so a label that wraps to two lines in a narrow column
              cannot knock its figure out of line with the two beside it — the
              KPI cells are read across, as a row. */}
            <div className='mt-1 grid grid-cols-3 items-end gap-2'>
              <DockKpi label={t('dockPanel.cut')} value={kpi[0]} color='var(--sv-cut)' />
              <DockKpi label={t('dockPanel.fill')} value={kpi[1]} color='var(--sv-fill)' />
              <DockKpi label={t('dockPanel.net')} value={kpi[2]} />
            </div>
            {/* The scope note and the chip are shared with `DeviationVolumePanel`
              (one component, so a copy fix cannot land on only one of them). */}
            {meta.stationVolumes && (
              <StationVolumesChip layerId={layerId} kind='surfaceDifference' summary={meta.stationVolumes.summary} />
            )}
            {/* Rendered only when `stats.expectation` exists — absent on every run
              made before this feature, and on any run made with mode NONE, so
              every existing run's panel is untouched (Task 5).

              This row USED to sit outside the collapse gate, so that a FAIL
              verdict could never be hidden by a collapse. That requirement has
              not gone away — it moved to the collapsed header summary above,
              where the verdict shows as a `VerdictBadge`. What is gated here is
              the seven metrics and the openable chip behind it, which is
              reference material for someone reading the card, not a headline. */}
            {e && (
              <div
                data-testid='sd-compliance'
                className={cn(
                  DOCK_PAD.section,
                  'flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-600 dark:text-stone-300',
                )}>
                {/* `meanThicknessM` is `null` when `measuredCells` is 0 — nothing
                  was measured, which is a state, never a zero (design doc §4.3).
                  Always rendered, same em-dash-for-null convention this file
                  already uses for `m3`/`m2`/`coveragePct` above, rather than
                  silently dropping the whole row. */}
                <Metric
                  label={t('expectation.meanThickness')}
                  value={e.meanThicknessM != null ? `${mToMm(e.meanThicknessM).toFixed(1)} mm` : '—'}
                />
                {/* Only CONSTANT mode has a real "design thickness" set by the
                  user. MATCH encodes its expectation as `thicknessM: 0.0` (a
                  sentinel meaning "should match exactly", not a measurement),
                  and SURFACES has no single scalar at all — printing either as
                  "Design thickness: 0.0 mm" would be exactly the placeholder
                  value the task forbids. */}
                {e.mode === 'CONSTANT' && e.thicknessM != null && (
                  <Metric label={t('expectation.design')} value={`${mToMm(e.thicknessM).toFixed(1)} mm`} />
                )}
                {/* The four shares are of MEASURED area and sum to 100; notMeasured
                  is of the whole grid. Two denominators in one row, so each is
                  labelled — design doc §4.1, "getting this wrong is how a
                  40%-covered layer reports 0% thin and passes". */}
                <Metric label={t('expectation.underPct')} value={`${e.underPct.toFixed(1)} %`} />
                <Metric label={t('expectation.inSpecPct')} value={`${e.inSpecPct.toFixed(1)} %`} />
                <Metric label={t('expectation.overPct')} value={`${e.overPct.toFixed(1)} %`} />
                {/* Hidden only when exactly zero — the common case, and the one
                  where a fourth number would be noise. Shown whenever non-zero:
                  the three shares above already sum to 100 on their own (design
                  doc §4.4), so a nonzero crossing share would otherwise be
                  counted against the verdict with no visible explanation. */}
                {e.crossingPct > 0 && (
                  <Metric label={t('expectation.crossingPct')} value={`${e.crossingPct.toFixed(1)} %`} />
                )}
                <Metric label={t('expectation.notMeasuredPct')} value={`${e.notMeasuredAreaPct.toFixed(1)} %`} />
                <VerdictChip verdict={e.verdict} criteria={e.criteria} />
              </div>
            )}
            <div className='mt-2 flex flex-col gap-2'>
              <div className='grid grid-cols-2 gap-1 text-slate-600 dark:text-stone-300'>
                <span>{t('dockPanel.area')}</span>
                <span className='text-right'>{m2(s.areaM2)}</span>
                <span>{t('surfaceDifference.panel.coverage')}</span>
                <span className='text-right'>{s.coveragePct?.toFixed(1) ?? '—'} %</span>
                <span>{t('dockPanel.resolution')}</span>
                {/* Two decimals, matching `DeviationVolumePanel`'s row: a bare
                    `{s.effectiveResolution}` printed `0.5 m` here and the raw
                    float elsewhere, and the deviation card printed the same
                    kind of number in centimetres. One unit, one precision. */}
                <span className='text-right'>{s.effectiveResolution.toFixed(2)} m</span>
              </div>
              {coarsened && (
                <p className='text-amber-600'>
                  {t('surfaceDifference.panel.coarsened', {
                    requested: s.requestedResolution,
                    effective: s.effectiveResolution.toFixed(3),
                  })}
                </p>
              )}
              <label className='flex items-center gap-2'>
                <Checkbox checked={vis.cut} onCheckedChange={(c) => setVis({ ...vis, cut: !!c })} />
                {t('surfaceDifference.panel.showCut')}
              </label>
              <label className='flex items-center gap-2'>
                <Checkbox checked={vis.fill} onCheckedChange={(c) => setVis({ ...vis, fill: !!c })} />
                {t('surfaceDifference.panel.showFill')}
              </label>
              {!!s.surfaces?.length && (
                <table className='w-full'>
                  <thead>
                    <tr className='text-left text-slate-500'>
                      <th className={DOCK_PAD.cell}>{t('surfaceDifference.panel.tableSurface')}</th>
                      <th className={cn(DOCK_PAD.cell, 'text-right')}>{t('dockPanel.cut')}</th>
                      <th className={cn(DOCK_PAD.cell, 'text-right')}>{t('dockPanel.fill')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.surfaces.map((r) => {
                      // Per ROW, not per cell: the same rule as the KPIs above,
                      // and the same one `DeviationVolumePanel`'s per-surface
                      // table already follows.
                      const cells = m3Row([r.cutVolumeM3, r.fillVolumeM3]);
                      return (
                        <tr key={r.featureId}>
                          <td className={DOCK_PAD.cell}>{r.featureName}</td>
                          <td className={cn(DOCK_PAD.cell, 'text-right')}>{cells[0]}</td>
                          <td className={cn(DOCK_PAD.cell, 'text-right')}>{cells[1]}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </DockPanel>
    </div>
  );
}

/** An inline label/value pair for the compliance row — deliberately not the
 *  stacked `DockKpi` the row above uses: the compliance row can hold up to
 *  seven of these plus the verdict chip on one flex-wrapped line, so each pair
 *  needs to read left-to-right rather than claim a whole grid cell. */
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className='inline-flex items-baseline gap-1'>
      <span>{label}:</span>
      <span className='font-medium tabular-nums text-slate-900 dark:text-stone-100'>{value}</span>
    </span>
  );
}
